import React, { useState, useCallback } from 'react';
import {
  Modal,
  Button,
  Icon,
  Text,
  BlockStack,
  Spinner,
} from '@shopify/polaris';
import {
  ChevronLeftMinor,
  ChevronRightMinor,
  ImageMajor,
} from '@shopify/polaris-icons';
import { authService } from '../../services/authService';

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const StoryPreviewModal = ({ 
  isOpen, 
  onClose, 
  selectedStory,
  setToastMessage, 
  setToastActive 
}) => {
  const [storyScenes, setStoryScenes] = useState([]);
  const [currentStoryImageIndex, setCurrentStoryImageIndex] = useState(0);
  const [isLoadingStoryScenes, setIsLoadingStoryScenes] = useState(false);

  const handleClose = () => {
    onClose();
    setCurrentStoryImageIndex(0); // Reset index when closing
  };

  // Load story scenes when modal opens
  const loadStoryScenes = useCallback(async () => {
    if (!selectedStory) return;
    
    setIsLoadingStoryScenes(true);
    setCurrentStoryImageIndex(0);
    
    try {
      const response = await fetch(`${SERVER_URL}/previewer-scenes/story/${selectedStory.slug}/`, {
        headers: authService.getAuthHeaders(),
      });
      
      if (!response.ok) {
        let errorData = {};
        try {
          errorData = await response.json();
        } catch (parseError) {
          console.error('Failed to parse error response:', parseError);
        }
        
        const errorMessage = errorData.error || errorData.message || `HTTP error! status: ${response.status}`;
        setToastMessage(`Story Preview Error: ${errorMessage}`);
        setToastActive(true);
        setTimeout(() => {
          setToastActive(false);
        }, 5000);
        return;
      }
      
      const result = await response.json();
      setStoryScenes(Array.isArray(result.data) ? result.data : []);
    } catch (error) {
      console.error('Failed to fetch story scenes:', error);
      const errorMessage = error.message || 'Failed to fetch story scenes';
      setToastMessage(`Story Preview Error: ${errorMessage}`);
      setToastActive(true);
      setTimeout(() => {
        setToastActive(false);
      }, 5000);
    } finally {
      setIsLoadingStoryScenes(false);
    }
  }, [selectedStory, setToastMessage, setToastActive]);

  // Load scenes when modal opens
  React.useEffect(() => {
    if (isOpen && selectedStory) {
      loadStoryScenes();
    }
  }, [isOpen, selectedStory, loadStoryScenes]);

  if (!selectedStory) return null;

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={<div style={{padding: '16px'}}>{`${selectedStory.title} - Story Scenes`}</div>}
      primaryAction={{
        content: 'Close',
        onAction: handleClose,
      }}
      footer={
        storyScenes.length > 1 ? (
          <div style={{padding: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px'}}>
            <Button
              icon={ChevronLeftMinor}
              size="slim"
              onClick={() => setCurrentStoryImageIndex(prev => 
                prev === 0 ? storyScenes.length - 1 : prev - 1
              )}
            />
            <Text variant="bodyMd" tone="subdued">
              {currentStoryImageIndex + 1} of {storyScenes.length}
            </Text>
            <Button
              icon={ChevronRightMinor}
              size="slim"
              onClick={() => setCurrentStoryImageIndex(prev => 
                prev === storyScenes.length - 1 ? 0 : prev + 1
              )}
            />
          </div>
        ) : (
          <div style={{padding: '16px'}}></div>
        )
      }
    >
      {isLoadingStoryScenes ? (
        <BlockStack gap="400" align="center">
          <Spinner size="large" />
          <Text variant="bodyMd" tone="subdued">Loading story scenes...</Text>
        </BlockStack>
      ) : storyScenes.length > 0 ? (
        <BlockStack gap="400">
          {/* Image Display */}
          <div style={{ 
            position: 'relative', 
            width: '100%', 
            height: '400px', 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            backgroundColor: '#f6f6f7',
            borderRadius: '8px',
            overflow: 'hidden'
          }}>
            <img 
              src={storyScenes[currentStoryImageIndex].scene_url} 
              alt={`Scene ${storyScenes[currentStoryImageIndex].order || currentStoryImageIndex + 1}`}
              style={{ 
                maxWidth: '100%', 
                maxHeight: '100%', 
                objectFit: 'contain' 
              }} 
            />
          </div>
          
          {/* Scene Info */}
          <div style={{ textAlign: 'center' }}>
            <Text variant="bodyMd">
              Scene {storyScenes[currentStoryImageIndex].order || currentStoryImageIndex + 1}
            </Text>
            <Text variant="bodyMd" tone="subdued">
              {storyScenes[currentStoryImageIndex].type || 'Scene'}
            </Text>
            {storyScenes[currentStoryImageIndex].text && (
              <Text variant="bodyMd" tone="subdued" style={{ marginTop: '8px' }}>
                {storyScenes[currentStoryImageIndex].text}
              </Text>
            )}
          </div>
        </BlockStack>
      ) : (
        <BlockStack gap="400" align="center">
          <Icon source={ImageMajor} color="subdued" />
          <Text variant="bodyMd" tone="subdued">No scenes available for this story</Text>
        </BlockStack>
      )}
    </Modal>
  );
};

export default StoryPreviewModal; 